import { useEffect, useRef } from "react";
import { Button } from "reactstrap";
import PropTypes from "prop-types";
import { useKeyboardShortcut } from "../hooks/useKeyboardShortcut";
import { useMapBearing } from "./NorthIndicator";

const SIZE = 140;
const CENTER = SIZE / 2;
const RADIUS = 56;
// No tick at 0: the N label sits there
const TICKS = Array.from({ length: 11 }, (_, i) => (i + 1) * 30);
const KEY_STEP = 5;

// Pointer angle around the dial centre, in degrees, clockwise from east
const pointerAngle = (event, rect) =>
  (Math.atan2(
    event.clientY - (rect.top + rect.height / 2),
    event.clientX - (rect.left + rect.width / 2)
  ) *
    180) /
  Math.PI;

// Compact ring at the bottom of the map. Dragging it turns the map by the angle
// swept around the ring's own centre.
export const RotationDial = ({ map, closing, onClose, onClosed }) => {
  const bearing = useMapBearing(map);
  const drag = useRef(null);
  const root = useRef(null);

  useKeyboardShortcut("escape", onClose);

  // Any press outside the dial (map, toolbox...) closes it. Capture phase, so
  // Leaflet stopping the propagation on its own controls does not hide it.
  useEffect(() => {
    const onDocumentPointerDown = (event) => {
      if (root.current.contains(event.target)) return;
      // The guided tour's tooltip points at the dial: its Next is not a click
      // away from it. Held down, it let the dial close before the click landed.
      if (event.target.closest(".react-joyride__tooltip")) return;
      onClose();
    };
    document.addEventListener("pointerdown", onDocumentPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onDocumentPointerDown, true);
  }, [onClose]);

  const onPointerDown = (event) => {
    const rect = event.currentTarget.ownerSVGElement.getBoundingClientRect();
    drag.current = {
      rect,
      startAngle: pointerAngle(event, rect),
      startBearing: map.getBearing(),
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!drag.current) return;
    const { rect, startAngle, startBearing } = drag.current;
    map.setBearing(startBearing + pointerAngle(event, rect) - startAngle);
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowRight") map.setBearing(bearing + KEY_STEP);
    if (event.key === "ArrowLeft") map.setBearing(bearing - KEY_STEP);
  };

  return (
    <div
      ref={root}
      className={`rotation-dial${closing ? " rotation-dial--closing" : ""}`}
      onAnimationEnd={() => closing && onClosed()}
    >
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width={SIZE}
        height={SIZE}
        style={{ transform: `rotate(${bearing}deg)` }}
      >
        <circle
          className="rotation-dial__ring"
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          tabIndex={0}
          role="slider"
          aria-label="Map bearing"
          aria-valuemin={0}
          aria-valuemax={359}
          aria-valuenow={Math.round(bearing) % 360}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={() => (drag.current = null)}
          onPointerCancel={() => (drag.current = null)}
          onKeyDown={onKeyDown}
        />
        {TICKS.map((angle) => (
          <line
            key={angle}
            className="rotation-dial__tick"
            x1={CENTER}
            y1={CENTER - RADIUS - 5}
            x2={CENTER}
            y2={CENTER - RADIUS + 5}
            transform={`rotate(${angle} ${CENTER} ${CENTER})`}
          />
        ))}
        <text
          className="rotation-dial__north"
          x={CENTER}
          y={CENTER - RADIUS + 4}
          textAnchor="middle"
        >
          N
        </text>
      </svg>
      <span className="rotation-dial__value">{Math.round(bearing) % 360}°</span>
      <Button
        className="rotation-dial__reset"
        color="primary"
        size="sm"
        onClick={() => map.setBearing(0)}
      >
        <i className="fa-solid fa-arrow-rotate-left me-1" />
        Reset
      </Button>
      <button
        type="button"
        className="btn-close rotation-dial__close"
        aria-label="Close"
        onClick={onClose}
      />
    </div>
  );
};

RotationDial.propTypes = {
  map: PropTypes.object.isRequired,
  closing: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onClosed: PropTypes.func.isRequired,
};
